import { Controller } from "@hotwired/stimulus"

// Drives the "generating" screen: animates the 4-step checklist while POSTing
// the generate request, then navigates to the review editor. The checklist is
// cosmetic pacing — the real work is the single POST; we advance steps on a
// timer and hold the last step until the request resolves.
export default class extends Controller {
  static targets = ["step", "error"]
  static values = { url: String, redirect: String, list: String }

  connect() {
    this.current = -1
    this.advance()
    this.timer = setInterval(() => this.advance(), 900)
    this.generate()
  }

  disconnect() {
    if (this.timer) clearInterval(this.timer)
  }

  advance() {
    // Mark the previous step done; activate the next (stop before the last so
    // it stays "active" until the request resolves).
    if (this.current >= 0) this.markDone(this.stepTargets[this.current])
    if (this.current < this.stepTargets.length - 1) {
      this.current += 1
      this.markActive(this.stepTargets[this.current])
    } else if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  markActive(step) {
    if (!step) return
    step.classList.remove("opacity-25", "opacity-50")
    step.classList.add("bg-app-surface", "ring-1", "ring-vivid-purple/30")
  }

  markDone(step) {
    if (!step) return
    step.classList.remove("bg-app-surface", "ring-1", "ring-vivid-purple/30")
    step.classList.add("opacity-50")
    const icon = step.querySelector('[data-role="icon"]')
    const check = step.querySelector('[data-role="check"]')
    if (icon) icon.classList.add("hidden")
    if (check) check.classList.remove("hidden")
  }

  async generate() {
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken(),
          Accept: "application/json",
        },
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok && data.ok) {
        // Let the checklist finish its last beat, then navigate.
        this.finish()
        setTimeout(() => {
          window.location = data.redirect || this.redirectValue
        }, 500)
      } else {
        this.fail(data.error || "Generation failed. Please try again.")
      }
    } catch (e) {
      this.fail("Couldn't reach the server. Please try again.")
    }
  }

  finish() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
    this.stepTargets.forEach((s) => this.markDone(s))
  }

  fail(message) {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
    setTimeout(() => {
      if (this.listValue) window.location = this.listValue
    }, 2500)
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
